// The workspace's canvases: each one the traces laid on it, which one anchors
// it, the reader's readings of its cells and the nodes they closed, and the
// chat it was worked in; which canvas is open. Kept in this browser across
// reloads. The canvas reads the open one to know what to draw, the pickers to
// mark what is already on it, the rail to list them, and the chat's context
// builder to tell the agent what the reader is looking at.
import { useSyncExternalStore } from "react";
import { answerSteps } from "./answerEvidence.js";

// What a hook hands useSyncExternalStore, made once: a subscribe or a read made
// anew on each render has React subscribe again, or read again, each time.
const storeOf = (subscribers, get) => ({ subscribe: (fn) => { subscribers.add(fn); return () => subscribers.delete(fn); }, get });

const KEY = "origin-workspace-v2";
const KEY_V1 = "origin-workspace-v1";
const MAX = 40;
export const MAX_WORKSPACE_TRACES = MAX;
const MAX_CANVASES = 24;

export const cleanTraces = (list) => (Array.isArray(list) ? list : [])
  .filter((t) => t && typeof t.id === "string" && t.id)
  .slice(0, MAX)
  .map((t) => ({ id: t.id.toLowerCase(), title: typeof t.title === "string" ? t.title : "", addedAt: Number(t.addedAt) || 0 }));

// The sizes the reader gave cards on the board, by session id: `m` how many
// times its board scale a card's width runs (held at its start in time, or,
// sized from its left, at its end: `pin`), `h` its height, `top` where down
// the board the reader put it.
export function cleanSizes(raw) {
  const out = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [id, sz] of Object.entries(raw)) {
    const m = Number.isFinite(sz?.m) && sz.m > 0 ? sz.m : null;
    const h = Number.isFinite(sz?.h) && sz.h > 0 ? sz.h : null;
    const pin = m != null && sz?.pin === "end" ? "end" : null;
    // A card moved along the board keeps its offset whether or not it was
    // sized; the pinned edge means something only for one sized sideways.
    const off = Number.isFinite(sz?.off) && sz.off !== 0 ? sz.off : null;
    // Folded to its head, whatever else it keeps.
    const fold = sz?.fold === true;
    // A pinned attachment set along its band: how far from its moment (pixels).
    const dx = Number.isFinite(sz?.dx) && Math.round(sz.dx) !== 0 ? Math.round(sz.dx) : null;
    // Put down the board by the reader: there, whatever else is on it.
    const top = Number.isFinite(sz?.top) && sz.top >= 0 ? Math.round(sz.top) : null;
    // Let go there by the reader, over whatever lies there (no packing moves it).
    const free = top != null && sz?.free === true;
    // Where it stands among cards laid over each other, the last carried on top.
    const z = top != null && Number.isInteger(sz?.z) && sz.z > 0 ? sz.z : null;
    if (m != null || h != null || off != null || fold || dx != null || top != null) {
      out[id] = { ...(m != null ? { m } : {}), ...(pin ? { pin } : {}), ...(off != null ? { off } : {}), ...(h != null ? { h } : {}), ...(fold ? { fold } : {}), ...(dx != null ? { dx } : {}), ...(top != null ? { top } : {}), ...(free ? { free } : {}), ...(z != null ? { z } : {}) };
    }
  }
  return out;
}

// The order the reader stacked the board's cards in, by session id: each
// takes the highest place it fits after the ones before it (its place along
// the board is its time, never set by hand). Cards not in it stack after.
// A machine card of the chat's graph (`graph:` and its group) and a pinned
// attachment's block (`pin:` and its id) keep their places in the order as a
// trace does.
const MAX_ORDER = 96;
export function cleanOrder(raw, traces) {
  const on = new Set(traces.map((t) => t.id));
  const seen = new Set();
  const out = [];
  for (const id of Array.isArray(raw) ? raw : []) {
    const text = String(id || "");
    const kept = text.startsWith("graph:") || text.startsWith("pin:");
    const key = kept ? text : text.toLowerCase();
    if ((on.has(key) || kept) && !seen.has(key)) { seen.add(key); out.push(key); }
  }
  return out.slice(0, MAX_ORDER);
}

// What the reader did on a board of `traces`: the readings of its cells and
// the nodes they closed, its notes, its cards' sizes and their stacking order.
export function cleanBoard(raw, traces) {
  return {
    readings: Array.isArray(raw?.readings) ? raw.readings.filter((r) => r && r.id && r.session && r.lane != null) : [],
    hidden: Array.isArray(raw?.hidden) ? raw.hidden.filter((h) => typeof h === "string") : [],
    notes: Array.isArray(raw?.notes) ? raw.notes.filter((n) => n && n.id && n.session && typeof n.text === "string") : [],
    sizes: cleanSizes(raw?.sizes),
    order: cleanOrder(raw?.order, traces),
  };
}

function cleanCanvas(raw, i) {
  const traces = cleanTraces(raw?.traces);
  return {
    id: typeof raw?.id === "string" && raw.id ? raw.id : `canvas-${Date.now().toString(36)}-${i}`,
    name: typeof raw?.name === "string" && raw.name ? raw.name : `Canvas ${i + 1}`,
    traces,
    anchor: traces.some((t) => t.id === raw?.anchor) ? raw.anchor : traces[0]?.id || null,
    ...cleanBoard(raw, traces),
    // Whether the canvas lays on the sessions its chat looks into, and the
    // ones the reader took off, which following never lays on again.
    follow: raw?.follow !== false,
    dismissed: (Array.isArray(raw?.dismissed) ? raw.dismissed : []).filter((id) => typeof id === "string" && id).map((id) => id.toLowerCase()).slice(-200),
    chatId: raw?.chatId != null ? String(raw.chatId) : null,
    createdAt: Number(raw?.createdAt) || Date.now(),
  };
}

function load() {
  try {
    const raw = JSON.parse(globalThis.localStorage?.getItem(KEY) || "null");
    if (raw && Array.isArray(raw.canvases) && raw.canvases.length) {
      const canvases = raw.canvases.slice(0, MAX_CANVASES).map(cleanCanvas);
      return { canvases, active: canvases.some((c) => c.id === raw.active) ? raw.active : canvases[0].id };
    }
    // The one canvas the first cut kept becomes the first of several.
    const v1 = JSON.parse(globalThis.localStorage?.getItem(KEY_V1) || "null");
    const first = cleanCanvas(v1 && Array.isArray(v1.traces) ? { ...v1, id: "canvas-1", name: "Canvas 1" } : { id: "canvas-1", name: "Canvas 1" }, 0);
    return { canvases: [first], active: first.id };
  } catch {
    const first = cleanCanvas({ id: "canvas-1", name: "Canvas 1" }, 0);
    return { canvases: [first], active: first.id };
  }
}

let state = load();
let activeCanvas = state.canvases.find((c) => c.id === state.active) || state.canvases[0];
const subscribers = new Set();

function commit(next) {
  state = next;
  activeCanvas = state.canvases.find((c) => c.id === state.active) || state.canvases[0];
  try { globalThis.localStorage?.setItem(KEY, JSON.stringify(state)); } catch { /* private window: this tab only */ }
  for (const fn of subscribers) fn();
}

// The open canvas, changed by `fn` (which returns the canvas's new fields).
function updateActive(fn) {
  const patch = fn(activeCanvas);
  if (!patch) return;
  commit({ ...state, canvases: state.canvases.map((c) => (c.id === activeCanvas.id ? { ...c, ...patch } : c)) });
}

function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

// The open canvas: { id, name, traces, anchor, readings, hidden, chatId }.
const getActiveCanvas = () => activeCanvas;
export function useWorkspace() {
  return useSyncExternalStore(subscribe, getActiveCanvas, getActiveCanvas);
}

export function workspaceSnapshot() {
  return activeCanvas;
}

// Every canvas and which one is open.
const getState = () => state;
export function useCanvases() {
  return useSyncExternalStore(subscribe, getState, getState);
}

// Lay traces on the open canvas, by session id, newest additions last. The
// first trace laid on a blank canvas anchors it.
export function addToWorkspace(entries) {
  const list = (Array.isArray(entries) ? entries : [entries])
    .map((e) => (typeof e === "string" ? { id: e } : e))
    .filter((e) => e?.id)
    .map((e) => ({ id: String(e.id).toLowerCase(), title: String(e.title || ""), addedAt: Date.now() }));
  updateActive((c) => {
    const have = new Set(c.traces.map((t) => t.id));
    const fresh = list.filter((e) => !have.has(e.id) && (have.add(e.id), true));
    if (!fresh.length) return null;
    const traces = [...c.traces, ...fresh].slice(-MAX);
    const back = new Set(fresh.map((e) => e.id));
    return {
      traces, anchor: c.anchor && traces.some((t) => t.id === c.anchor) ? c.anchor : traces[0].id,
      dismissed: (c.dismissed || []).filter((id) => !back.has(id)),
    };
  });
}

export function removeFromWorkspace(id) {
  const key = String(id || "").toLowerCase();
  updateActive((c) => {
    const traces = c.traces.filter((t) => t.id !== key);
    if (traces.length === c.traces.length) return null;
    const { [key]: _dropped, ...sizes } = c.sizes || {};
    return {
      traces, anchor: c.anchor === key ? traces[0]?.id || null : c.anchor, readings: c.readings.filter((r) => r.session !== key),
      notes: c.notes.filter((n) => n.session !== key), sizes, order: (c.order || []).filter((x) => x !== key),
      dismissed: [...(c.dismissed || []).filter((x) => x !== key), key].slice(-200),
    };
  });
}

export function setWorkspaceAnchor(id) {
  const key = String(id || "").toLowerCase();
  updateActive((c) => (!c.traces.some((t) => t.id === key) || c.anchor === key ? null : { anchor: key }));
}

export function titleWorkspaceTrace(id, title) {
  const key = String(id || "").toLowerCase();
  updateActive((c) => {
    const t = c.traces.find((x) => x.id === key);
    if (!t || !title || t.title === title) return null;
    return { traces: c.traces.map((x) => (x.id === key ? { ...x, title } : x)) };
  });
}

export function clearWorkspace() {
  updateActive((c) => ({
    traces: [], anchor: null, readings: [], hidden: [], notes: [], sizes: {}, order: [],
    dismissed: [...new Set([...(c.dismissed || []), ...c.traces.map((t) => t.id)])].slice(-200),
  }));
}

// Notes pinned on the open canvas's cells ({ id, session, messageId?, turn?,
// lane?, text, source, at }): the chat's, left by workspace_point.
export function addCanvasNote(note) {
  if (!note?.id || !note.session) return;
  updateActive((c) => ({ notes: [...c.notes.filter((n) => n.id !== note.id), note].slice(-60) }));
}

export function setCanvasNotes(notes) {
  updateActive((c) => (c.notes === notes ? null : { notes: Array.isArray(notes) ? notes : [] }));
}

// The open canvas's readings of cells, and the annotation nodes it closed.
export function setCanvasReadings(readings) {
  updateActive((c) => (c.readings === readings ? null : { readings: Array.isArray(readings) ? readings : [] }));
}

export function setCanvasHidden(ids) {
  updateActive((c) => (c.hidden === ids ? null : { hidden: Array.isArray(ids) ? ids : [] }));
}

// The sizes the reader gave the open canvas's cards.
export function setCanvasSizes(sizes) {
  updateActive((c) => (c.sizes === sizes ? null : { sizes: cleanSizes(sizes) }));
}

// Whether the open canvas lays on the sessions its chat looks into.
// Following again lays on every session the chat has looked into, the ones
// taken off included.
export function setCanvasFollow(on) {
  updateActive((c) => (c.follow === !!on ? null : { follow: !!on, ...(on ? { dismissed: [] } : {}) }));
}

// The order the reader stacked the open canvas's cards in.
export function setCanvasOrder(order) {
  updateActive((c) => (c.order === order ? null : { order: cleanOrder(order, c.traces) }));
}

// A new, blank canvas, opened.
export function newCanvas({ name = null } = {}) {
  const n = state.canvases.length;
  const used = new Set(state.canvases.map((c) => c.name));
  let k = n + 1;
  while (used.has(`Canvas ${k}`)) k += 1;
  const canvas = cleanCanvas({ id: `canvas-${Date.now().toString(36)}`, name: name || `Canvas ${k}` }, n);
  commit({ canvases: [...state.canvases, canvas].slice(-MAX_CANVASES), active: canvas.id });
  return canvas.id;
}

export function switchCanvas(id) {
  if (id === state.active || !state.canvases.some((c) => c.id === id)) return;
  commit({ ...state, active: id });
}

export function renameCanvas(id, name) {
  const clean = String(name || "").trim();
  if (!clean) return;
  commit({ ...state, canvases: state.canvases.map((c) => (c.id === id ? { ...c, name: clean } : c)) });
}

export function deleteCanvas(id) {
  const canvases = state.canvases.filter((c) => c.id !== id);
  if (canvases.length === state.canvases.length) return;
  if (!canvases.length) canvases.push(cleanCanvas({ id: `canvas-${Date.now().toString(36)}`, name: "Canvas 1" }, 0));
  commit({ canvases, active: state.active === id ? canvases[canvases.length - 1].id : state.active });
}

// The chat a canvas is worked in, so opening the canvas opens its chat.
export function setCanvasChat(id, chatId) {
  const value = chatId != null ? String(chatId) : null;
  const c = state.canvases.find((x) => x.id === id);
  if (!c || c.chatId === value) return;
  commit({ ...state, canvases: state.canvases.map((x) => (x.id === id ? { ...x, chatId: value } : x)) });
}

// The trace last taken off the open canvas, for a moment, so it can be put
// back as it was: { sid, title, wasAnchor, readings, notes, size }. Kept here,
// not in the canvas, because taking off the anchor remounts the canvas.
let removal = null;
let removalTimer = null;
const removalSubscribers = new Set();
function setRemoval(next) {
  removal = next;
  clearTimeout(removalTimer);
  if (next) removalTimer = setTimeout(() => setRemoval(null), 8000);
  for (const fn of removalSubscribers) fn();
}
export function recordRemoval(snapshot) {
  setRemoval(snapshot ? { ...snapshot, at: Date.now() } : null);
}
export function clearRemoval() {
  setRemoval(null);
}
const lastRemovalStore = storeOf(removalSubscribers, () => removal);
export function useLastRemoval() {
  return useSyncExternalStore(lastRemovalStore.subscribe, lastRemovalStore.get, lastRemovalStore.get);
}

// What the chat is told about the open canvas, one line.
export function describeWorkspace(s = activeCanvas) {
  if (!s?.traces?.length) return "workspace canvas is empty (workspace_lay_on lays sessions on it)";
  const items = s.traces.slice(0, 12).map((t) => `${t.id === s.anchor ? "anchor " : ""}session ${t.id}${t.title ? ` ("${t.title.slice(0, 80)}")` : ""}`);
  const notes = s.notes?.length ? `; ${s.notes.length} note${s.notes.length === 1 ? "" : "s"} pinned` : "";
  return `workspace canvas holds ${s.traces.length} trace${s.traces.length === 1 ? "" : "s"}: ${items.join("; ")}${s.traces.length > 12 ? "; ..." : ""}${notes} (workspace_state reads it, workspace_lay_on adds sessions, workspace_point pins a note on a turn)`;
}

// What the canvas knows that the sidebar lists: each card's hue and title, and
// the sessions related to the anchor (the index search's, and the chat's), for
// the reader to lay on. Published by the canvas while it is up.
let canvasState = null;
const canvasSubscribers = new Set();

export function publishWorkspaceCanvas(next) {
  canvasState = next || null;
  for (const fn of canvasSubscribers) fn();
}

export function workspaceCanvasSnapshot() {
  return canvasState;
}

const workspaceCanvasStore = storeOf(canvasSubscribers, () => canvasState);
export function useWorkspaceCanvas() {
  return useSyncExternalStore(workspaceCanvasStore.subscribe, workspaceCanvasStore.get, workspaceCanvasStore.get);
}

// A request to bring one trace's card into view, from outside the canvas. The
// count makes a repeated request for the same trace a new one.
let focusState = { id: null, n: 0 };
const focusSubscribers = new Set();

export function requestWorkspaceFocus(id) {
  focusState = { id: String(id || "").toLowerCase() || null, n: focusState.n + 1 };
  for (const fn of focusSubscribers) fn();
}

const workspaceFocusStore = storeOf(focusSubscribers, () => focusState);
export function useWorkspaceFocus() {
  return useSyncExternalStore(workspaceFocusStore.subscribe, workspaceFocusStore.get, workspaceFocusStore.get);
}

// What is about the traces on the board in front, opened or closed from
// outside it (a chat's workspace_open): `n` counts asks, so the same ask again
// is a new one.
let openAsk = { ids: [], open: true, n: 0 };
const openAskSubscribers = new Set();

export function askWorkspaceOpen(ids, open = true) {
  openAsk = { ids: [...(ids || [])].map(String), open: !!open, n: openAsk.n + 1 };
  for (const fn of openAskSubscribers) fn();
}

export function workspaceOpenAsk() {
  return openAsk;
}

const workspaceOpenAskStore = storeOf(openAskSubscribers, () => openAsk);
export function useWorkspaceOpenAsk() {
  return useSyncExternalStore(workspaceOpenAskStore.subscribe, workspaceOpenAskStore.get, workspaceOpenAskStore.get);
}

// What the board in front lists of its chat's claims, from outside it: the
// claim refs a chat answer cites ([c5]) asked from a click on one (`ids`, the
// claims that ref names; `n` counts asks, so the same ref again is a new one);
// the claims the board can show, for the chat to link only refs to them; and
// the chat's latest answer with the graph it rests on, for the board showing
// that graph to list the claims in the answer's order.
let evidenceAsk = { text: "", ids: [], n: 0 };
let boardClaims = null;
let chatAnswer = { graph: null, text: "", key: "" };
const evidenceSubscribers = new Set();
const emitEvidence = () => { for (const fn of evidenceSubscribers) fn(); };
const subscribeEvidence = (fn) => { evidenceSubscribers.add(fn); return () => evidenceSubscribers.delete(fn); };

export function askClaimEvidence(text, ids) {
  evidenceAsk = { text: String(text || ""), ids: [...(ids || [])].map((id) => String(id).toLowerCase()), n: evidenceAsk.n + 1 };
  emitEvidence();
}

const getEvidenceAsk = () => evidenceAsk;
export function useClaimEvidenceAsk() {
  return useSyncExternalStore(subscribeEvidence, getEvidenceAsk, getEvidenceAsk);
}

export function publishBoardClaims(ids) {
  const next = ids?.size ? ids : null;
  if (next === boardClaims) return;
  boardClaims = next;
  emitEvidence();
}

const getBoardClaims = () => boardClaims;
export function useBoardClaims() {
  return useSyncExternalStore(subscribeEvidence, getBoardClaims, getBoardClaims);
}

// What the board lists of an answer: the claims it cites in its order, each
// with the words it says them in. While it streams in (`done` false) only the
// claims are compared, so the board is drawn again as the answer cites another
// claim, not for each word; once it is done, its words too. `at` says which
// answer it is (lib/answerEvidence.js latestAnswer).
function answerKey(text, at, done) {
  const steps = answerSteps(text);
  return `${at ?? ""}|${done ? JSON.stringify(steps) : steps.map((s) => s.claims.join(",")).join(";")}`;
}

export function publishChatAnswer(graph, text, { at = null, done = true } = {}) {
  const next = { graph: graph || null, text: String(text || "") };
  const key = answerKey(next.text, at, done);
  if (next.graph === chatAnswer.graph && key === chatAnswer.key) return;
  chatAnswer = { ...next, key };
  emitEvidence();
}

const getChatAnswer = () => chatAnswer;
export function useChatAnswer() {
  return useSyncExternalStore(subscribeEvidence, getChatAnswer, getChatAnswer);
}

// A note on the board in front pointed at or opened from outside the board
// (a chat's list of the pins it put there): `open` counts asks, so a note
// asked again is a new ask. And the notes whose pins the board's pointer is
// on, for such a list to light.
let noteAsk = { point: null, open: { id: null, n: 0 } };
const noteAskSubscribers = new Set();
const emitNoteAsk = () => { for (const fn of noteAskSubscribers) fn(); };

export function pointWorkspaceNote(id) {
  const point = id ? String(id) : null;
  if (noteAsk.point === point) return;
  noteAsk = { ...noteAsk, point };
  emitNoteAsk();
}

export function openWorkspaceNote(id) {
  noteAsk = { ...noteAsk, open: { id: id ? String(id) : null, n: noteAsk.open.n + 1 } };
  emitNoteAsk();
}

const workspaceNoteAskStore = storeOf(noteAskSubscribers, () => noteAsk);
export function useWorkspaceNoteAsk() {
  return useSyncExternalStore(workspaceNoteAskStore.subscribe, workspaceNoteAskStore.get, workspaceNoteAskStore.get);
}

// A message to open on a board from outside it (a finding opened from the
// welcome page's lists, at its message), for the board in front whose
// session it is: its cell picked and read in the dock. It is asked as its
// board is opened, before the board is up, so a board coming in front takes
// an ask still open (`done` false); one asked over MESSAGE_ASK_MS ago is let
// go.
const MESSAGE_ASK_MS = 30_000;
let messageAsk = { session: null, messageId: null, n: 0, at: 0, done: true };
const messageAskSubscribers = new Set();
const emitMessageAsk = () => { for (const fn of messageAskSubscribers) fn(); };

export function askWorkspaceMessage(session, messageId) {
  const s = String(session || "").toLowerCase();
  const m = String(messageId || "").toLowerCase();
  if (!s || !m) return;
  messageAsk = { session: s, messageId: m, n: messageAsk.n + 1, at: Date.now(), done: false };
  emitMessageAsk();
}

export function settleWorkspaceMessage(n) {
  if (messageAsk.n !== n || messageAsk.done) return;
  messageAsk = { ...messageAsk, done: true };
  emitMessageAsk();
}

export const messageAskOpen = (ask, nowMs = Date.now()) => !!ask && !ask.done && nowMs - ask.at <= MESSAGE_ASK_MS;

export function workspaceMessageAsk() {
  return messageAsk;
}

const workspaceMessageAskStore = storeOf(messageAskSubscribers, () => messageAsk);
export function useWorkspaceMessageAsk() {
  return useSyncExternalStore(workspaceMessageAskStore.subscribe, workspaceMessageAskStore.get, workspaceMessageAskStore.get);
}

let notesPointed = null;
const notesPointedSubscribers = new Set();

export function publishWorkspaceNotesPointed(ids) {
  const next = ids?.length ? ids : null;
  if (next === notesPointed || (next && notesPointed && next.length === notesPointed.length && next.every((id, i) => id === notesPointed[i]))) return;
  notesPointed = next;
  for (const fn of notesPointedSubscribers) fn();
}

const workspaceNotesPointedStore = storeOf(notesPointedSubscribers, () => notesPointed);
export function useWorkspaceNotesPointed() {
  return useSyncExternalStore(workspaceNotesPointedStore.subscribe, workspaceNotesPointedStore.get, workspaceNotesPointedStore.get);
}
